import { RecordActivity, RecordItem, RecordModel, StockModel } from "../../../shared/utils/cropr";
import useCroprBackbone from "../../../shared/hooks/useCroprBackbone";
import moment from "moment";

export type CostsAggregateType = "day" | "week" | "month";

type Costs = number;

interface AggregateData {
  mDate: moment.Moment;
  costs: Costs;
}

export interface CostsData {
  aggregate: CostsAggregateType;
  data: AggregateData[];
  total: number;
}

export interface useCostsDataProps {
  aggregate?: CostsAggregateType;
}
const NumberOfBars = 7; //NOTE: Do not increase beyond 12, since our lookup uses date/week/month numbers

const getAggregateIndex = (aggregate: CostsAggregateType, mDate: moment.Moment): number => {
  switch (aggregate) {
    case "day":
      return mDate.date();
    case "week":
      return mDate.week();
    case "month":
      return mDate.month();
  }
};
const useCostsData = ({ aggregate = "day" }: useCostsDataProps): CostsData => {
  let unit: "days" | "weeks" | "months";
  switch (aggregate) {
    case "day":
      unit = "days";
      break;
    case "week":
      unit = "weeks";
      break;
    case "month":
      unit = "months";
  }
  const [, recordsCollection] = useCroprBackbone<RecordModel>("records");
  const [, stocksCollection] = useCroprBackbone<StockModel>("stock");
  const to = moment(),
    from = to
      .clone()
      .subtract(NumberOfBars - 1, unit)
      .hour(0)
      .minute(0)
      .second(0)
      .milliseconds(0); // set to zero is needed because we use .diff() later
  const myFrom = from.clone();
  const data: AggregateData[] = Array(NumberOfBars)
    .fill(0)
    .map((_, i) => ({
      mDate: myFrom.clone().add(i, unit),
      costs: 0,
    }));
  let LUT: { [index: number]: number } = {};
  data.forEach((val, i) => (LUT[getAggregateIndex(aggregate, val.mDate)] = i));
  recordsCollection.forEach((rec: RecordModel) => {
    const d = moment(rec.get("date"));
    if (!d.isBetween(from, to, unit, "[]") || rec.get("processing_state") !== "performed") return;
    const bin = LUT[getAggregateIndex(aggregate, d)];
    // {type: 1000, stock: 358452, theme: 10, attributes: Array(1), items, seedling_stock}
    rec.get("activities").forEach((activity: RecordActivity) => {
      activity.items?.forEach((item: RecordItem) => {
        // Fertilizer/GBM/Others
        if (item.value && item.stock) {
          const cost = stocksCollection.get(item.stock).get("cost_per_unit") || 0;
          const total = item.value * cost * rec.get("worked_area");
          data[bin].costs += total;
        }
      });
      // Sowing
      if (activity.seedling_stock && activity.attributes) {
        activity.attributes.forEach((attribute) => {
          // QUANTITY_PER_HECTARE = 1000602
          if (attribute.type === 1000602 && attribute.value) {
            const cost =
              (activity.seedling_stock &&
                stocksCollection.get(activity.seedling_stock).get("cost_per_unit")) ||
              0;
            const total = attribute.value * cost * rec.get("worked_area");
            data[bin].costs += total;
          }
        });
      }
    });
  });
  const total = data.reduce((total, data) => (total += data.costs), 0);
  return {
    aggregate,
    data,
    total,
  };
};

export default useCostsData;
